import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import {
  applyEvent,
  FacilityKind,
  FormClassApply,
  Prefectures,
} from "../../assets/data/Constants";
import TkKikoStepper from "../../components/molecules/TkKikoStepper";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkViewButtonJudgement from "../../components/molecules/TkViewButtonJudgement";
import TkMemo from "../../components/molecules/TkMemo";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import { TkGetCode } from "../../utils/TkGetCode";
import { ITkViewButtonJudgementStatus } from "../../type/componentsType/TkViewButtonJudgement";
import { httpAPI_APP016, httpAPI_FAC025 } from "./api/request";
import { headList, tableList } from "./parts";
import "./_style.scss";
import { changeZipcodeFormat } from "../../utils/useTools";
import TkTableWithNoLine from "../../components/molecules/TkTableWithNoLine";
import TkDownloadLink from "../../components/atoms/TkDownloadLink";
import _ from "lodash";

/**
 * SCRW050004 施設情報の変更登録
 * 機構職員が施設基本情報を更新する。また申請の受理・承認等を行う。
 * @return SCRW050004
 */
const SCRW050004 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext(BusinessContext);
  const [stateTableList, setStateTableList] = useState<any>([]);
  const tableListRef = useRef(_.cloneDeep(tableList));
  const [memoBodyList, setMemoBodyList] = useState<any>([]);
  const [fileList, setFileList] = useState<any>([[]]);
  const [status, setStatus] = useState<ITkViewButtonJudgementStatus>();
  const [memo, setMemo] = useState("");
  const [check, setCheck] = useState("");
  const applyId = location.state?.applyId;

  const callApiInit = async () => {
    let resArr = tableListRef.current;

    // 施設情報の変更登録表示API実行
    try {
      const res = await httpAPI_FAC025({
        contractorNo: businessState.common?.contractorNo,
        applyId,
      });

      if (res.resultCd === "I0000") {
        let facilityName = resArr.find(
          (item) => item.keyword === "facilityName"
        )!;
        let bAddress = resArr.find((item) => item.keyword === "bAddress")!;
        let bFacilityClassCd = resArr.find(
          (item) => item.keyword === "bFacilityClassCd"
        )!;
        let bZipcode = resArr.find((item) => item.keyword === "bZipcode")!;

        setStatus(res.latestApplyStatus! as ITkViewButtonJudgementStatus);

        const facilityList = res.facilityList ? res.facilityList[0] : undefined;

        // 施設名称
        facilityName.selectPropsList = [
          {
            label: `${facilityList?.facilityNo}:${facilityList?.bFacilityName}`,
            value: `${facilityList?.facilityNo}`,
          },
        ];
        facilityName.setDefaultValue = facilityList?.facilityNo;

        // 初期表示
        for (let i in facilityList) {
          let targetObj = resArr.find((item) => item.keyword === i)!;

          targetObj &&
            (targetObj.setDefaultValue =
              facilityList[i as keyof typeof facilityList]);
          targetObj &&
            (targetObj.text = facilityList[i as keyof typeof facilityList]);
        }
        // 旧：郵便番号
        bZipcode.text = changeZipcodeFormat(facilityList?.bZipcode);

        // 旧：所在地
        bAddress.text = `${TkGetCode(
          Prefectures,
          facilityList?.bPrefectureCd
        )}${facilityList?.bCity}${facilityList?.bAdressOthers}`;

        // 新：所在地
        resArr.find((item) => item.keyword === "aAddress")!.valueObject = {
          prefectures: TkGetCode(Prefectures, facilityList?.aPrefectureCd),
          city: facilityList?.aCity!,
          address: facilityList?.aAdressOthers!,
        };

        // 旧：施設種類
        bFacilityClassCd.text =
          FacilityKind[
            facilityList?.bFacilityClassCd as keyof typeof FacilityKind
          ];
        // 添付書類
        res.formList?.forEach((item) => {
          if (item.formClass === "2") {
            resArr.find((item) => item.keyword === "form")!.showLine =
              !!applyId;
            resArr.find((item) => item.keyword === "form")!.base64filedata =
              item.fileBase64;
            resArr.find((item) => item.keyword === "form")!.dataname =
              item.fileName;
          }
        });

        // メモ欄
        let bodyList = res.statusList?.map((item: any) => [
          {
            children: item.managerName,
          },
          {
            children: item.process,
          },
          {
            children: item.createDate,
            align: "center",
          },
          {
            children: item.processComment,
          },
        ]);

        // 共済契約者氏名等変更届・共済契約証書再交付申請書
        let file = res.formList?.find(
          (item) => item.formClass === "EXP0601013"
        );
        let fileList = [
          [
            {
              children: (
                <TkDownloadLink
                  dataname={TkGetCode(FormClassApply, file?.formClass)}
                  base64filedata={file?.fileBase64 ?? ""}
                  downloadBase64
                />
              ),
            },
          ],
        ];

        let changes = resArr.find((item) => item.keyword === "changes")!;
        let changesList: any[] = [];
        const changeMappings = [
          {
            condition: facilityList?.nameChangeDate,
            range: [6, 9],
            message: "施設名称の変更",
          },
          {
            condition: facilityList?.addressChangeDate,
            range: [9, 14],
            message: "施設の所在地の変更",
          },
          {
            condition: facilityList?.classChangeDate,
            range: [14, 19],
            message: "施設種類の変更",
          },
        ];
        changeMappings.forEach(({ condition, range, message }) => {
          if (condition) {
            resArr.forEach((item, index) => {
              if (index >= range[0] && index < range[1]) {
                changesList.push(message);
                item.showLine = true;
              }
            });
          }
        });
        changes.setDefaultValue = changesList;

        setFileList(fileList);

        setMemoBodyList(bodyList);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }

      setStateTableList([...resArr]);
    } catch (error) {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    let tableArr = tableListRef.current;
    let contractorName = tableArr.find(
      (item) => item.keyword === "contractorName"
    )!;
    contractorName.contractorNumber = `${businessState.common?.contractorNo}:${businessState.common?.contractorName}`;
    setStateTableList([...tableArr]);

    callApiInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMemoChange = (e: any) => {
    if (e.target.type === "input") {
      setMemo(e.target.value);
    } else {
      setCheck(e.target.value ? "2" : "");
    }
  };

  const handleSubmit = async (event: keyof typeof applyEvent) => {
    const applyEventNo = applyEvent[event];

    if (
      ![
        applyEvent["受理"],
        applyEvent["契約者に差戻し"],
        applyEvent["上長承認"],
        applyEvent["担当者に差戻し"],
        applyEvent["課長代理確認"],
      ].includes(applyEventNo)
    )
      return;

    // 申請状況更新API実行
    try {
      const res = await httpAPI_APP016({
        applyId,
        applyEvent: applyEventNo,
        contractorNo: businessState?.common?.contractorNo,
        reason: memo,
        screenId: "SCRW050004",
        inquiryCode: check,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        navigate("/SCRW110001");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box data-testid="SCRW050004Test" className="SCRW050004">
      <TkKikoStepper
        applicationFlowType="TYPE3"
        businessType="JOB-19"
        applicationStatus={status}
      />

      <TkFakeTable list={stateTableList} ruleSchema={[]} showButton={false} />

      <TkMemo bodyList={memoBodyList} onChange={handleMemoChange} />

      <TkTableWithNoLine headList={headList} bodyList={fileList} />

      <Box className="button">
        <TkBackButton className="tkBottonMr" />
        <TkViewButtonJudgement
          Status={status}
          flowType="TYPE3"
          onClick={handleSubmit}
        />
      </Box>
    </Box>
  );
};

export default SCRW050004;
